import CompanyMobileCard from 'components/card/MobileListCard/CompanyMobileCard';
import MobileListSkeleton from 'components/card/MobileListCard/MobileListSkeleton';
import CompaniesToolbar from 'components/companies/CompaniesToolbar';
import CompanyFilterPanel from 'components/companies/CompanyFilterPanel';
import CompanyTable from 'components/companies/CompanyTable';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PageForm from 'components/pageForm/PageForm';
import Pagination from 'components/pagination/pagination';
import useSlideOver from 'components/SlideOver/useSlideOver';
import useCompaniesChanged from 'hooks/useCompaniesChanged';
import useCompanyFilterOptions from 'hooks/useCompanyFilterOptions';
import useCompanyFromUrl from 'hooks/useCompanyFromUrl';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MediaQuery from 'react-responsive';
import { toast } from 'react-toastify';
import { COMPANIES_PER_PAGE } from 'server/crm/crmCompanies';
import crmApi from 'server/crm/crmApi';
import { NO_FILTERS, activeFilterCount, assignErrorText, describeCompany } from './companiesItems';

// The departments chosen stay from one visit to the next: an agency works on
// its own island.
const DEPARTMENTS_KEY = 'companies-departments';
const storedDepartments = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(DEPARTMENTS_KEY));
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};
const storeDepartments = (departments) => {
  try {
    localStorage.setItem(DEPARTMENTS_KEY, JSON.stringify(departments));
  } catch {
    // Storage unavailable: the choice is not kept.
  }
};

/**
 * « Entreprises » of the CRM: the companies of Jobbiz in four tabs, the
 * prospects, the new clients (less than 90 days), the older ones and the lost
 * companies, each with its sales rep and the signed-in user's own next action,
 * a page of 20 at a time from admin/crm/companies. A row opens the company's
 * page, its group the group's.
 */
const CompaniesPage = () => {
  const { text, language } = useText('companies');
  const { call, me, ready } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const options = useCompanyFilterOptions();
  const navigate = useNavigate();
  // An old link `?idCompany=` still opens the company's panel.
  const { selectedId } = useCompanyFromUrl();
  const openCompany = (companyId) => navigate(`/company/${companyId}`);
  const openGroup = (groupId) => navigate(`/company/group/${groupId}`);
  const [tab, setTab] = useState('prospects');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(() => ({ ...NO_FILTERS, departments: storedDepartments() }));
  const [page, setPage] = useState(1);
  // Bumped when a company is created elsewhere: the page reads its rows again.
  const [version, setVersion] = useState(0);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // The people a company can be given to; the rows wait for them, their
  // avatars with them.
  const [team, setTeam] = useState(null);
  const [filtering, setFiltering] = useState(false);
  const latest = useRef(0);

  // The search waits for the typing to pause.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  // Only the latest answer counts: a slow search must not overwrite the tab
  // picked since.
  const load = useCallback(() => {
    const request = latest.current + 1;
    latest.current = request;
    setBusy(true);
    setFailed(false);
    return call(crmApi.listCompanies, { tab, search, filters, page }).then(
      (next) => {
        if (request !== latest.current) return;
        // The rows keep the tab they were read for until the next ones come.
        setData({ ...next, tab });
        setBusy(false);
      },
      (error) => {
        if (request !== latest.current || error?.name === 'CanceledError') return;
        setBusy(false);
        setFailed(true);
      },
    );
  }, [call, tab, search, filters, page, version]);

  useEffect(() => {
    if (ready) load();
  }, [load, ready]);
  useEffect(() => {
    if (ready) call(crmApi.getTeam).then(setTeam, () => setTeam([]));
  }, [call, ready]);
  useCompaniesChanged(() => setVersion((current) => current + 1));

  const pickTab = (next) => {
    setTab(next);
    setPage(1);
  };
  const changeFilters = (change) => {
    setFilters((current) => ({ ...current, ...change }));
    setPage(1);
    if (change.departments) storeDepartments(change.departments);
  };

  const assign = async (company, role, personId) => {
    try {
      await call(crmApi.assignCompany, { companyId: company._id, role, personId });
      const person = team?.find((member) => member.id === personId);
      const name = `${person?.firstname ?? ''} ${person?.lastname ?? ''}`.trim();
      toast.success(text(role === 'recruiter' ? 'recruiterAssigned' : 'commercialAssigned', { company: company.name, name }));
      load();
    } catch (error) {
      toast.error(assignErrorText(error, text));
    }
  };

  const now = new Date();
  const shownTab = data?.tab ?? tab;
  const companies =
    data && team
      ? data.companies.map((company) =>
          describeCompany(company, { tab: shownTab, now, text, language, categories: options.categories }),
        )
      : null;
  const filterCount = activeFilterCount(filters);
  const emptyState = () => {
    if (search || filterCount) {
      return { glyph: 'searchX', title: text('emptyFilteredTitle'), description: text('emptyFilteredText') };
    }
    return { glyph: 'inbox', title: text('emptyTitle'), description: text(`empty_${shownTab}`) };
  };

  return (
    <PageForm>
      <div className="flex min-h-0 flex-1 flex-col">
        <CompaniesToolbar
          tab={tab}
          prospectCount={data?.counts.prospects}
          query={query}
          filterCount={filterCount}
          onTab={pickTab}
          onQuery={setQuery}
          onFilter={() => setFiltering(true)}
          onAdd={() => toggleSlideOver('add_company')}
        />
        <main className="flex min-h-0 flex-1 flex-col border-t border-gray-100">
          {failed && (
            <CompactEmptyState title={text('loadError')} action={{ label: text('retry'), onClick: load }} />
          )}
          {!failed && companies?.length === 0 && <CompactEmptyState {...emptyState()} />}
          {!failed && (!companies || companies.length > 0) && (
            <>
              <MediaQuery minWidth={768}>
                <CompanyTable
                  tab={shownTab}
                  companies={companies}
                  busy={busy && Boolean(companies)}
                  selectedId={selectedId}
                  team={team ?? []}
                  meId={me?.id}
                  onOpen={openCompany}
                  onOpenGroup={openGroup}
                  onAssign={assign}
                />
              </MediaQuery>
              <MediaQuery maxWidth={767}>
                {companies ? (
                  <div className="flex flex-col gap-3 overflow-y-auto p-4">
                    {companies.map((company, index) => (
                      <div
                        key={company._id}
                        className="motion-safe:animate-fadeUp"
                        style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}
                      >
                        <CompanyMobileCard
                          company={company}
                          tab={shownTab}
                          isSelected={company._id === selectedId}
                          onOpen={openCompany}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <MobileListSkeleton />
                )}
              </MediaQuery>
            </>
          )}
        </main>
        {!failed && data && data.pages > 1 && (
          <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 py-4 md:px-8">
            <Pagination
              currentPage={data.page}
              countPerPage={COMPANIES_PER_PAGE}
              countTotal={data.count}
              totalPages={data.pages}
              setCurrentPage={setPage}
            />
          </div>
        )}
      </div>
      <CompanyFilterPanel
        isOpen={filtering}
        onClose={() => setFiltering(false)}
        filters={filters}
        onChange={changeFilters}
        options={options}
        team={team ?? []}
        meId={me?.id}
      />
    </PageForm>
  );
};

export default CompaniesPage;
